import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hoaApi, maintenanceApi, paymentsApi, noticesApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import BuildingSwitcher from '../components/BuildingSwitcher';
import RoleSwitcher from '../components/RoleSwitcher';
import {
  Building2,
  Users,
  Home,
  Bell,
  FileText,
  Wrench,
  Calendar,
  CreditCard,
  Package,
  Vote,
  BarChart3,
  Settings,
  LogOut,
  Plus,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Menu,
  X,
  Receipt,
  Search,
  User,
  Shield,
  Hammer,
  Briefcase,
  MessageSquare,
  Gavel,
  PieChart as PieChartIcon,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const sidebarItems = [
  { icon: BarChart3, label: 'Dashboard', path: '/admin' },
  { icon: Bell, label: 'Notices', path: '/admin/notices' },
  { icon: FileText, label: 'Documents', path: '/admin/documents' },
  { icon: Wrench, label: 'Maintenance', path: '/admin/maintenance' },
  { icon: Receipt, label: 'Financials', path: '/admin/financials' },
  { icon: PieChartIcon, label: 'Financial Dashboard', path: '/admin/financial-dashboard' },
  { icon: TrendingUp, label: 'Analytics Engine', path: '/admin/analytics' },
  { icon: Shield, label: 'Insurance', path: '/admin/insurance' },
  { icon: Users, label: 'Residents & Units', path: '/admin/residents' },
  { icon: Shield, label: 'User Management', path: '/admin/user-management' },
  { icon: Briefcase, label: 'Contractors', path: '/admin/contractors' },
  { icon: Hammer, label: 'Providers', path: '/admin/providers' },
  { icon: MessageSquare, label: 'Trustee Hub', path: '/trustee-hub' },
  { icon: Vote, label: 'Voting', path: '/admin/voting' },
  { icon: Gavel, label: 'Governance', path: '/governance' },
  { icon: Settings, label: 'Operations', path: '/operations' },
  { icon: User, label: 'Profile', path: '/dashboard/profile' },
  { icon: Settings, label: 'Settings', path: '/admin/settings' },
];

const COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#EF4444'];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hoaStats, setHoaStats] = useState(null);
  const [maintenanceStats, setMaintenanceStats] = useState(null);
  const [financialStats, setFinancialStats] = useState(null);
  const [recentNotices, setRecentNotices] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [managingAgent, setManagingAgent] = useState(null);

  const hoaId = user?.hoa_id || 'demo_hoa';
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Demo data for when no HOA is set
        if (!user?.hoa_id) {
          setHoaStats({
            units_count: 48,
            residents_count: 92,
            pending_tickets: 7,
            active_notices: 5,
            upcoming_events: 3,
            pending_parcels: 12,
            total_invoices: 48,
            paid_invoices: 41,
            collection_rate: 85.4,
          });
          setMaintenanceStats({
            pending: 4,
            in_progress: 3,
            completed: 28,
            total: 35,
            categories: { plumbing: 12, electrical: 8, general: 10, hvac: 5 },
          });
          setFinancialStats({
            total_invoiced: 24000,
            total_collected: 20500,
            outstanding: 35000,
            collection_rate: 85.4,
            pending_count: 7,
            overdue_count: 2,
            currency: 'ZAR',
            monthly_data: [
              { month: 'Aug', invoiced: 40000, collected: 38000 },
              { month: 'Sep', invoiced: 40000, collected: 35000 },
              { month: 'Oct', invoiced: 40000, collected: 39000 },
              { month: 'Nov', invoiced: 40000, collected: 32000 },
              { month: 'Dec', invoiced: 40000, collected: 31000 },
              { month: 'Jan', invoiced: 40000, collected: 30000 },
            ],
          });
          setRecentNotices([
            { notice_id: '1', title: 'Water Maintenance Schedule', category: 'maintenance', created_at: new Date().toISOString() },
            { notice_id: '2', title: 'AGM Meeting Reminder', category: 'event', created_at: new Date().toISOString() },
          ]);
          setRecentTickets([
            { ticket_id: '1', title: 'Elevator malfunction', status: 'pending', priority: 'high', category: 'electrical' },
            { ticket_id: '2', title: 'Pool pump not working', status: 'in_progress', priority: 'medium', category: 'general' },
            { ticket_id: '3', title: 'Parking gate stuck', status: 'pending', priority: 'high', category: 'electrical' },
          ]);
          // Demo managing agent
          setManagingAgent({ company_name: 'PropManage SA' });
          setLoading(false);
          return;
        }

        // Fetch managing agent for this building
        const token = localStorage.getItem('session_token');
        try {
          const agentRes = await fetch(`${API_URL}/api/hoa/${hoaId}/managing-agent`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (agentRes.ok) {
            const agentData = await agentRes.json();
            setManagingAgent(agentData);
          }
        } catch (e) {
          // No managing agent assigned
        }

        const [hoaData, maintenanceData, financialData, noticesData, ticketsData] = await Promise.all([
          hoaApi.getStats(hoaId).catch(() => null),
          maintenanceApi.getStats(hoaId).catch(() => null),
          paymentsApi.getStats(hoaId).catch(() => null),
          noticesApi.list(hoaId).catch(() => []),
          maintenanceApi.list(hoaId).catch(() => []),
        ]);

        setHoaStats(hoaData);
        setMaintenanceStats(maintenanceData);
        setFinancialStats(financialData);
        setRecentNotices(noticesData.slice(0, 3));
        setRecentTickets(ticketsData.slice(0, 5));
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [hoaId, user?.hoa_id]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatCurrency = (amount, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-sky-100 text-sky-700',
      completed: 'bg-emerald-100 text-emerald-700',
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'border-slate-200',
      medium: 'border-amber-300',
      high: 'border-red-300',
      urgent: 'border-red-500 bg-red-50',
    };
    return colors[priority] || colors.medium;
  };

  const pieData = maintenanceStats ? [
    { name: 'Pending', value: maintenanceStats.pending, color: '#F59E0B' },
    { name: 'In Progress', value: maintenanceStats.in_progress, color: '#0EA5E9' },
    { name: 'Completed', value: maintenanceStats.completed, color: '#10B981' },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link to="/admin" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src="/logo.png" alt="BodyCorp365" className="w-10 h-10" />
              <div>
                <p className="font-bold font-manrope text-white">BodyCorp365</p>
                <p className="text-xs text-slate-400">Admin Portal</p>
              </div>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-slate-400"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`sidebar-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* User */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-sky-600 text-white">
                  {user?.name?.charAt(0) || 'A'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role || 'Property Manager'}</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-slate-400 hover:text-white"
                onClick={handleLogout}
                data-testid="sidebar-logout"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-40 lg:hidden glass border-b border-slate-200/50">
        <div className="flex items-center justify-between px-4 py-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-sky-500 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold font-manrope text-slate-900">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <BuildingSwitcher compact />
            <RoleSwitcher />
            <Button variant="ghost" size="icon" onClick={() => navigate('/notifications')}>
              <Bell className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold font-manrope text-slate-900">Dashboard</h1>
                <p className="text-slate-500 mt-1">Welcome back! Here's your community overview.</p>
              </div>
              <div className="hidden lg:flex items-center gap-2">
                <BuildingSwitcher />
              </div>
              {managingAgent && (
                <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="managed-by-badge">
                  <Briefcase className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-indigo-700">
                    Managed by <span className="font-semibold">{managingAgent.company_name}</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Invite Managing Agent CTA (only if no agent linked) */}
          {!managingAgent && hoaId && (
            <div
              className="mb-6 p-4 rounded-xl bg-gradient-to-r from-indigo-50 to-emerald-50 border border-indigo-200 flex flex-col sm:flex-row items-start sm:items-center gap-3"
              data-testid="invite-agent-cta"
            >
              <Briefcase className="w-6 h-6 text-indigo-600 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold">Invite Your Managing Agent</div>
                <div className="text-sm text-gray-600">
                  Link your property management company for better communication, documents, maintenance and insurance.
                </div>
              </div>
              <Button
                onClick={() => navigate(`/admin/buildings/${hoaId}/invite-agent`)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="invite-agent-btn"
              >
                Get Started
              </Button>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
            {loading ? (
              <>
                <Skeleton className="h-28 rounded-xl" />
                <Skeleton className="h-28 rounded-xl" />
                <Skeleton className="h-28 rounded-xl" />
                <Skeleton className="h-28 rounded-xl" />
              </>
            ) : (
              <>
                <Card data-testid="stat-units">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Units</p>
                        <p className="text-2xl lg:text-3xl font-bold font-manrope text-slate-900">
                          {hoaStats?.units_count || 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center">
                        <Home className="w-6 h-6 text-sky-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-residents">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Residents</p>
                        <p className="text-2xl lg:text-3xl font-bold font-manrope text-slate-900">
                          {hoaStats?.residents_count || 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                        <Users className="w-6 h-6 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-tickets">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Open Tickets</p>
                        <p className="text-2xl lg:text-3xl font-bold font-manrope text-slate-900">
                          {hoaStats?.pending_tickets || 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                        <Wrench className="w-6 h-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-collection">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Collection Rate</p>
                        <p className="text-2xl lg:text-3xl font-bold font-manrope text-slate-900">
                          {hoaStats?.collection_rate || financialStats?.collection_rate || 0}%
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Financial Chart */}
            <Card data-testid="financial-chart">
              <CardHeader>
                <CardTitle className="font-manrope">Financial Overview</CardTitle>
                <CardDescription>Monthly levy collection</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-64 rounded-lg" />
                ) : financialStats?.monthly_data ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={financialStats.monthly_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          borderRadius: '8px', 
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }} 
                      />
                      <Bar dataKey="invoiced" fill="#94a3b8" radius={[4, 4, 0, 0]} name="Invoiced" />
                      <Bar dataKey="collected" fill="#0EA5E9" radius={[4, 4, 0, 0]} name="Collected" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Maintenance Chart */}
            <Card data-testid="maintenance-chart">
              <CardHeader>
                <CardTitle className="font-manrope">Maintenance Status</CardTitle>
                <CardDescription>Current ticket breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-64 rounded-lg" />
                ) : maintenanceStats ? (
                  <div className="flex items-center justify-center gap-8">
                    <ResponsiveContainer width={180} height={180}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-3">
                      {pieData.map((item) => (
                        <div key={item.name} className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-sm text-slate-600">{item.name}</span>
                          <span className="text-sm font-semibold text-slate-900">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-slate-400">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bottom Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Tickets */}
            <Card data-testid="recent-tickets">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-manrope">Recent Tickets</CardTitle>
                  <CardDescription>Latest maintenance requests</CardDescription>
                </div>
                <Link to="/admin/maintenance">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-16 rounded-lg" />
                    <Skeleton className="h-16 rounded-lg" />
                    <Skeleton className="h-16 rounded-lg" />
                  </div>
                ) : recentTickets.length > 0 ? (
                  <div className="space-y-3">
                    {recentTickets.map((ticket) => (
                      <div 
                        key={ticket.ticket_id}
                        className={`flex items-center justify-between p-3 rounded-lg border-l-4 bg-slate-50 ${getPriorityColor(ticket.priority)}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                            <Wrench className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{ticket.title}</p>
                            <p className="text-sm text-slate-500">{ticket.category}</p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(ticket.status)}>
                          {ticket.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-8">No recent tickets</p>
                )}
              </CardContent>
            </Card>

            {/* Financial Summary */}
            <Card data-testid="financial-summary">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-manrope">Financial Summary</CardTitle>
                  <CardDescription>Current billing status</CardDescription>
                </div>
                <Link to="/admin/financial-dashboard">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-12 rounded-lg" />
                    <Skeleton className="h-12 rounded-lg" />
                    <Skeleton className="h-12 rounded-lg" />
                  </div>
                ) : financialStats ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-50">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium text-slate-900">Collected</span>
                      </div>
                      <span className="font-bold text-emerald-600">
                        {formatCurrency(financialStats.total_collected, financialStats.currency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg bg-amber-50">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-slate-900">Pending ({financialStats.pending_count})</span>
                      </div>
                      <span className="font-bold text-amber-600">
                        {formatCurrency(financialStats.outstanding, financialStats.currency)}
                      </span>
                    </div>
                    {financialStats.overdue_count > 0 && (
                      <div className="flex items-center justify-between p-4 rounded-lg bg-red-50">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                          <span className="font-medium text-slate-900">Overdue ({financialStats.overdue_count})</span>
                        </div>
                        <Button size="sm" variant="destructive" className="rounded-full">
                          Send Reminders
                        </Button>
                      </div>
                    )}
                    <div className="pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-500">Collection Progress</span>
                        <span className="text-sm font-medium text-slate-900">{financialStats.collection_rate}%</span>
                      </div>
                      <Progress value={financialStats.collection_rate} className="h-2" />
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-8">No financial data</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
